import { ScoProduct } from '@src/graphql-types'
import { windowProxy } from '@src/hooks/useWindow'
import React from 'react'
import slugify from 'slugify'

if (windowProxy.trackerHistory === undefined) {
  windowProxy.trackerHistory = []
}

const linkedInPixels = [
  {
    eventLabel: 'FG RAC Submitted',
    pid: 2831274,
    conversionId: 3644866,
  },
  {
    eventLabel: 'FG Purchase',
    pid: 2831274,
    conversionId: 3717506,
  },
]

type AnalyticsProduct = {
  brand?: string
  category?: string
  coupon?: string
  currency?: string
  discount?: number | string
  item_id?: string
  name?: string
  position?: number | string
  price?: number | string
  product_id?: string
  sku?: string
  trackerLabel?: string
  variant?: string
  revenue?: number | string
  quantity?: number | string
}

function promisedTrack(label: string = 'Event', properties: any = {}, options: any = {}) {
  windowProxy.trackerHistory.push({ label, properties, options })
  console.log(`Track Event: ${label}`, properties)
  return new Promise(resolve => {
    try {
      if (windowProxy.analytics && windowProxy.analytics.track) {
        windowProxy.analytics.track(label, properties, options, resolve)
        setTimeout(resolve, 100)
      } else {
        resolve(true)
      }
    } catch (err) {
      resolve(false)
    }
  })
}

function mapProductToSegmentProduct(product: ScoProduct, index = 0): AnalyticsProduct {
  const discount =
    product.effectivePricingInCents &&
    product.pricingInCents &&
    (product.pricingInCents.signup - product.effectivePricingInCents.signup || 0) / 100

  const price = product.effectivePricingInCents?.signup / 100 || 0

  return {
    brand: 'Startups.com',
    category: 'Startups.com',
    coupon: product.appliedCoupon && product.appliedCoupon.code,
    currency: 'USD',
    discount: discount,
    name: `${product.productId}`,
    position: index,
    price: price,
    revenue: price,
    product_id: product.productId,
    item_id: product.productId,
    sku: product.productId,
    variant: product.effectiveTermId,
    quantity: 1,
  }
}

function getProductAmountCents(product?: ScoProduct): number {
  return product?.effectivePricingInCents?.signup ?? product?.pricingInCents?.signup ?? 0
}

const tracker = {
  _fbq: (...args) => {
    // console.log({ _fbq: args })
    if (windowProxy._fbq) {
      windowProxy._fbq(...args)
    }
  },
  oaiq: (action = 'measure', eventName = 'custom', eventProps = {}, eventOptions?: any) => {
    if (windowProxy.oaiq) {
      try {
        const args: any[] = [action, eventName, eventProps]
        if (eventOptions != null) {
          args.push(eventOptions)
        }
        windowProxy.oaiq(...args)
      } catch (err) {
        console.error('oaiq', err)
      }
    } else {
      console.log('oaiq', { action, eventName, eventProps, eventOptions })
    }
  },
  trackerHistory: windowProxy.trackerHistory,
  promisedTrack,
  identify: ({ company, email, firstName, lastName, userId }) => {
    console.log(`Track Identity: ${userId}`)
    return new Promise(resolve => {
      if (windowProxy.analytics && windowProxy.analytics.track) {
        windowProxy.analytics.identify(
          userId,
          { company, email, firstName, lastName, scoUserId: userId },
          {},
          resolve
        )
      } else {
        resolve(true)
      }
    })
  },

  pageViewed: () => {
    windowProxy.analytics.page()
    tracker.oaiq('measure', 'page_viewed', {
      type: 'contents',
      contents: [
        {
          id: `page_${slugify(windowProxy.location?.pathname?.replaceAll('/', '_')) || 'index'}`,
          name: windowProxy.document?.title,
          content_type: 'page',
        },
      ],
    })
  },

  /** `properties` (e.g. which funnel / path) are added to the Segment event only. */
  funnelStep: (step, properties = {}) => {
    // tracker._fbq('trackCustom', `FunnelStep${step}`)
    tracker.oaiq('measure', 'custom', { type: 'custom' }, { custom_event_name: `FunnelStep${step}` })
    return promisedTrack(`FunnelStep${step}`, {
      ...properties,
      category: 'Funnel',
      action: 'Step',
      label: `Funnel Step: ${step}`,
      value: 0,
    })
  },

  emailSubmitted: (properties = {}) => {
    tracker.oaiq(
      'measure',
      'custom',
      {
        type: 'custom',
      },
      {
        custom_event_name: 'EmailSubmitted',
      }
    )
    return promisedTrack('EmailSubmitted', {
      ...properties,
      category: 'Signup',
      action: 'Email Captured',
      label: 'Email Captured',
      value: 0,
    })
  },

  emailCaptured: (traits = {}, isSignup = true) => {
    tracker.oaiq('measure', 'lead_created', {
      type: 'customer_action',
    })
    return promisedTrack('Email Captured', {
      ...traits,
      category: 'Signup',
      action: 'Email Captured',
      label: 'Email Captured',
      value: 0,
    })
  },

  accountRegistered: (traits = {}) => {
    tracker.oaiq('measure', 'registration_completed', {
      type: 'customer_action',
    })
    return promisedTrack('Account Registered', {
      ...traits,
      category: 'Signup',
      action: 'Register',
      label: 'Account Registered',
      value: 0,
    })
  },

  s0Viewed: (traits = {}) => {
    return promisedTrack('S0 Viewed', {
      ...traits,
      category: 'Signup',
      action: 'View',
      label: 'S0',
      value: 0,
    })
  },
  s0Submitted: (traits = {}) => {
    return promisedTrack('S0 Submitted', {
      ...traits,
      category: 'Signup',
      action: 'Submit',
      label: 'S0',
      value: 0,
    })
  },
  s1Viewed: (traits = {}) => {
    return promisedTrack('S1 Viewed', {
      ...traits,
      category: 'Signup',
      action: 'View',
      label: 'S1',
      value: 0,
    })
  },
  s2Viewed: (traits = {}) => {
    return promisedTrack('S2 Viewed', {
      ...traits,
      category: 'Signup',
      action: 'View',
      label: 'S2',
      value: 0,
    })
  },
  productBuilderViewed: (traits = {}) => {
    return promisedTrack('Product Builder Viewed', {
      ...traits,
      category: 'Signup',
      action: 'View',
      label: 'Product Builder Viewed',
    })
  },
  productBuilderCompleted: (traits = {}) => {
    return promisedTrack('Product Builder Completed', {
      ...traits,
      category: 'Signup',
      action: 'Submit',
      label: 'Product Builder Completed',
    })
  },
  recommendedProductViewed: (traits = {}) => {
    return promisedTrack('Recommended Product Viewed', {
      ...traits,
      category: 'Signup',
      action: 'View',
      label: 'Recommended Product Viewed',
    })
  },
  s1Submitted: (traits = {}) => {
    return promisedTrack('S1 Submitted', {
      ...traits,
      category: 'Signup',
      action: 'Submit',
      label: 'S1',
      value: 0,
    })
  },
  s2Submitted: (traits = {}) => {
    return promisedTrack('S2 Submitted', {
      ...traits,
      category: 'Signup',
      action: 'Submit',
      label: 'S2',
      value: 0,
    })
  },

  accountRegistrationStarted: (traits = {}) =>
    promisedTrack('Account Registration Started', {
      ...traits,
      category: 'Signup',
      action: 'Register',
      label: 'Account Registration Started',
      value: 0,
    }),

  loggedIn: (traits = {}) =>
    promisedTrack('Logged In', {
      ...traits,
      category: 'Account',
      action: 'Log In',
      label: 'Logged In',
      value: 0,
    }),

  articleSharedToFacebook: () =>
    promisedTrack('Facebook Share', {
      category: 'Social Shares',
      action: 'Facebook Share',
      label: 'Article Page',
      value: 0,
    }),
  articleSharedToTwitter: () =>
    promisedTrack('Twitter Share', {
      category: 'Social Shares',
      action: 'Twitter Share',
      label: 'Article Page',
      value: 0,
    }),
  articleSharedToLinkedIn: () =>
    promisedTrack('LinkedIn Share', {
      category: 'Social Shares',
      action: 'LinkedIn Share',
      label: 'Article Page',
      value: 0,
    }),
  articleSharedToEmail: () =>
    promisedTrack('Email Share', {
      category: 'Social Shares',
      action: 'Email Share',
      label: 'Article Page',
      value: 0,
    }),
  accountSignupNavItemClicked: () =>
    promisedTrack('Click Signup Link', {
      category: 'Account Signup',
      action: 'Click Signup Link',
      label: 'Top Nav',
      value: 0,
    }),
  accountSignupCommentClicked: () =>
    promisedTrack('Click Signup Link', {
      category: 'Account Signup',
      action: 'Click Signup Link',
      label: 'Comments Using Email',
      value: 0,
    }),
  commentPosted: ({ entityType }) => {
    if (entityType === 'wp_startups_articles') {
      return tracker.commentPostedOnArticle()
    }
  },
  commentPostedOnArticle: () =>
    promisedTrack('Post a Comment', {
      category: 'Comments',
      action: 'Post a Comment',
      label: 'Article Page',
      value: 0,
    }),
  commentReplyPosted: ({ entityType }) => {
    if (entityType === 'wp_startups_articles') {
      return tracker.commentReplyPostedOnArticle()
    }
  },
  commentReplyPostedOnArticle: () =>
    promisedTrack('Replies', {
      category: 'Comments',
      action: 'Replies',
      label: 'Article Page',
      value: 0,
    }),
  lessonSharedToFacebook: () =>
    promisedTrack('Facebook Share', {
      category: 'Social Shares',
      action: 'Facebook Share',
      label: 'Education Lesson',
      value: 0,
    }),
  lessonSharedToTwitter: () =>
    promisedTrack('Twitter Share', {
      category: 'Social Shares',
      action: 'Twitter Share',
      label: 'Education Lesson',
      value: 0,
    }),
  lessonSharedToLinkedIn: () =>
    promisedTrack('LinkedIn Share', {
      category: 'Social Shares',
      action: 'LinkedIn Share',
      label: 'Education Lesson',
      value: 0,
    }),
  lessonSharedToEmail: () =>
    promisedTrack('Email Share', {
      category: 'Social Shares',
      action: 'Email Share',
      label: 'Education Lesson',
      value: 0,
    }),
  /* Bootcamp tracking */
  bootcampS0Submitted: ({ email, source }) =>
    promisedTrack('Bootcamp S0 Submitted', {
      email,
      category: 'Bootcamp - Signup',
      action: 'Bootcamp S0 Submitted',
      label: `Bootcamp S0 Sbmitted - ${source || 'Email'}`,
      value: 0,
    }),
  bootcampS1Submitted: ({ email, source }) =>
    promisedTrack('Bootcamp S1 Submitted', {
      email,
      category: 'Bootcamp - Signup',
      action: 'Bootcamp S1 Submitted',
      label: `Bootcamp S1 Sbmitted - ${source || 'Email'}`,
      value: 0,
    }),

  /* Bundle tracking */
  bundleS0Submitted: (email, source) =>
    promisedTrack('Bundle S0 Submitted', {
      email,
      category: 'Bundle - Signup',
      action: 'S0 Submitted',
      label: `S0 Sbmitted - ${source || 'Email'}`,
      value: 0,
    }),

  productListViewed: ({ products, productTags }) =>
    promisedTrack('Product List Viewed', {
      list_id: productTags && productTags.join && productTags.join('_'),
      category: productTags && productTags.join && productTags.join('_'),
      products:
        products &&
        products.map &&
        products.map((product, index) => mapProductToSegmentProduct(product, index)),
    }),

  productSelected: ({ product }: { product: ScoProduct }) =>
    promisedTrack('Product Added', {
      ...mapProductToSegmentProduct(product),
    }),

  checkoutStarted: ({
    product,
    products,
    intent,
  }: {
    product: ScoProduct
    products?: ScoProduct[]
    /** Conversion intent that opened the checkout (the founder moment behind the paywall). */
    intent?: string | null
  }) => {
    const mappedProducts: AnalyticsProduct[] = products
      ? products.map((p, index) => mapProductToSegmentProduct(p, index))
      : [mapProductToSegmentProduct(product)]
    const mappedProduct = mappedProducts[0] || {}
    const primaryProduct = products?.[0] || product
    const amountCents = getProductAmountCents(primaryProduct)

    tracker.oaiq('measure', 'checkout_started', {
      type: 'contents',
      amount: amountCents,
      currency: 'USD',
      contents: [
        {
          id: mappedProduct.product_id || mappedProduct.item_id || 'unknown',
          name: mappedProduct.name || 'Product',
          content_type: 'product',
          quantity: 1,
        },
      ],
    })

    return promisedTrack('Checkout Started', {
      coupon: mappedProduct.coupon,
      currency: 'USD',
      discount: mappedProduct.discount || 0,
      products: mappedProducts,
      value: mappedProduct.price,
      revenue: mappedProduct.price,
      category: 'Paywall Intent',
      action: `${mappedProduct.trackerLabel} Selected`,
      label: 'Conversion Path',
      paywall_intent: intent || undefined,
    })
  },

  paymentInfoEntered: ({ paymentMethod }) =>
    promisedTrack('Payment Info Entered', {
      payment_method: paymentMethod,
    }),

  orderCompleted: ({ subscriptionId, product }: { subscriptionId?: string; product?: ScoProduct }) => {
    const mappedProduct = mapProductToSegmentProduct(product)
    const amountCents = getProductAmountCents(product)
    const tracks = []

    const orderOptions = subscriptionId ? { event_id: subscriptionId } : undefined
    tracker.oaiq(
      'measure',
      'order_created',
      {
        type: 'contents',
        amount: amountCents,
        currency: 'USD',
        contents: [
          {
            id: mappedProduct.product_id || mappedProduct.item_id || 'unknown',
            name: mappedProduct.name || 'Product',
            content_type: 'product',
            quantity: 1,
          },
        ],
      },
      orderOptions
    )

    tracks.push(
      promisedTrack('purchase', {
        transaction_id: subscriptionId,
        value: mappedProduct.price,
        currency: 'USD',
        coupon: mappedProduct.coupon,
        items: [mappedProduct],
      })
    )

    tracks.push(
      promisedTrack('Order Completed', {
        checkout_id: subscriptionId,
        coupon: mappedProduct.coupon,
        currency: 'USD',
        discount: mappedProduct.discount,
        order_id: subscriptionId,
        products: [mappedProduct],
        revenue: mappedProduct.price,
        total: mappedProduct.price,
        category: 'Customer',
        action: 'Payment Success',
        label: 'Conversion Path',
      })
    )

    return Promise.all(tracks)
  },

  couponApplied: ({ couponCode }: { couponCode?: string }) =>
    promisedTrack('Coupon Applied', {
      coupon_id: couponCode,
      coupon_name: couponCode,
      category: 'Signup',
      action: 'Coupon Applied',
      label: 'Conversion Path',
    }),

  couponDenied: ({ couponCode }: { couponCode?: string }) =>
    promisedTrack('Coupon Denied', {
      coupon_id: couponCode,
      coupon_name: couponCode,
      category: 'Signup',
      action: 'Coupon Denied',
      label: 'Conversion Path',
    }),

  couponRemoved: ({ couponCode }: { couponCode?: string }) =>
    promisedTrack('Coupon Removed', {
      coupon_id: couponCode,
      coupon_name: couponCode,
      category: 'Signup',
      action: 'Coupon Removed',
      label: 'Conversion Path',
    }),

  ctaEvent: ({ action, category, label }) =>
    promisedTrack(action, {
      category,
      action,
      label,
    }),

  facRACSubmitted: (traits = {}) => {
    return promisedTrack('FAC RAC Submitted', {
      ...traits,
      category: 'FAC',
      action: 'RAC',
      label: 'FAC RAC Submitted',
      value: 0,
    })
  },

  fgRACSubmitted: (traits = {}) => {
    if (windowProxy.lintrk) {
      windowProxy.lintrk('track', { conversion_id: 7121892 })
    }
    return promisedTrack('FG RAC Submitted', {
      ...traits,
      category: 'FG',
      action: 'RAC',
      label: 'FG RAC Submitted',
      value: 0,
    })
  },

  memberMatchLPViewed: (traits: { variantKey?: string } = {}) => {
    return promisedTrack(`Member Match - LP Viewed (${traits?.variantKey || 'default'})`, {
      ...traits,
    })
  },

  memberMatchBuilderViewed: (traits: any = {}) => {
    return promisedTrack('Member Match - Builder Viewed', {
      ...traits,
    })
  },

  memberMatchBuilderModified: (traits: any = {}) => {
    return promisedTrack('Member Match - Builder Modified', {
      ...traits,
    })
  },

  memberMatchBuilderSubmitClicked: (traits: any = {}) => {
    return promisedTrack('Member Match - Builder Submit Clicked', {
      ...traits,
    })
  },

  memberMatchBuilderSubmitted: (traits: any = {}) => {
    return promisedTrack('Member Match - Builder Submitted', {
      ...traits,
    })
  },

  memberMatchEmailCaptureViewed: (traits: any = {}) => {
    return promisedTrack('Member Match - Email Capture Viewed', {
      ...traits,
    })
  },

  memberMatchSocialLoginClicked: (traits: any = {}) => {
    return promisedTrack('Member Match - Social Login Clicked', {
      ...traits,
    })
  },

  memberMatchEmailCaptured: (traits: any = {}) => {
    return promisedTrack('Member Match - Email Captured', {
      ...traits,
    })
  },

  memberMatchLPCTAClicked: (traits: any = {}) => {
    return promisedTrack('Member Match - LP CTA Clicked', {
      ...traits,
    })
  },

  memberMatchResultsViewed: (traits: any = {}) => {
    return promisedTrack('Member Match - Results Viewed', {
      ...traits,
    })
  },

  investorMatchingLPViewed: (traits: { variantKey?: string } = {}) => {
    return promisedTrack(`Investor Matching - LP Viewed (${traits?.variantKey || 'default'})`, {
      ...traits,
    })
  },

  investorMatchingPitchDeckViewed: (traits = {}) => {
    return promisedTrack(`Investor Matching - Pitch Deck Viewed`, {
      ...traits,
    })
  },
  investorMatchingPitchDeckSubmitted: (traits = {}) => {
    return promisedTrack(`Investor Matching - Pitch Deck Submitted`, {
      ...traits,
    })
  },

  investorMatchingBuilderViewed: (traits: any = {}) => {
    return promisedTrack('Investor Matching - Builder Viewed', {
      ...traits,
    })
  },

  investorMatchingBuilderModified: (traits: any = {}) => {
    return promisedTrack('Investor Matching - Builder Modified', {
      ...traits,
    })
  },

  investorMatchingBuilderSubmitClicked: (traits: any = {}) => {
    return promisedTrack('Investor Matching - Builder Submit Clicked', {
      ...traits,
    })
  },

  investorMatchingBuilderSubmitted: (traits: any = {}) => {
    return promisedTrack('Investor Matching - Builder Submitted', {
      ...traits,
    })
  },

  investorMatchingEmailCaptureViewed: (traits: any = {}) => {
    return promisedTrack('Investor Matching - Email Capture Viewed', {
      ...traits,
    })
  },

  investorMatchingRegisterViewed: (traits: any = {}) => {
    return promisedTrack('Investor Matching - Register Viewed', {
      ...traits,
    })
  },

  investorMatchingRegisterSubmitted: (traits: any = {}) => {
    return promisedTrack('Investor Matching - Register Submitted', {
      ...traits,
    })
  },

  investorMatchingSocialLoginClicked: (traits: any = {}) => {
    return promisedTrack('Investor Matching - Social Login Clicked', {
      ...traits,
    })
  },

  investorMatchingEmailCaptured: (traits: any = {}) => {
    return promisedTrack('Investor Matching - Email Captured', {
      ...traits,
    })
  },

  investorMatchingLPCTAClicked: (traits: any = {}) => {
    return promisedTrack('Investor Matching - LP CTA Clicked', {
      ...traits,
    })
  },

  investorMatchingResultsViewed: (traits: any = {}) => {
    return promisedTrack('Investor Matching - Results Viewed', {
      ...traits,
    })
  },

  investorMatchingPurchase: (traits: any = {}) => {
    return promisedTrack('Investor Matching - Purchase', {
      ...traits,
    })
  },
  investorMatchingTrialStarted: (traits: any = {}) => {
    return promisedTrack('Investor Matching - Trial Started', {
      ...traits,
    })
  },

  fgPurchased: ({ product }) => {
    const mappedProduct = mapProductToSegmentProduct(product)
    if (windowProxy.lintrk) {
      windowProxy.lintrk('track', { conversion_id: 7121884 })
    }
    return promisedTrack('FG Purchase', {
      total: mappedProduct.price,
      category: 'FG',
      action: 'Purchase',
      label: 'FG Purchase',
    })
  },

  renderPixels: () => {
    const pixelsToRender = linkedInPixels.filter(p =>
      windowProxy.trackerHistory?.find(e => e.label === p.eventLabel)
    )

    // console.log('pixelsToRender', pixelsToRender);
    // console.log('trackerHistory',  trackerHistory);

    if (pixelsToRender.length > 0) {
      windowProxy.trackerHistory = []
    }

    return (
      <div id={'linkedin-pixels'}>
        {pixelsToRender.map((p, i) => (
          <img
            key={i}
            alt={''}
            height={'1'}
            src={`https://px.ads.linkedin.com/collect/?pid=${p.pid}&conversionId=${p.conversionId}&fmt=gif`}
            style={{ display: 'none' }}
            width={'1'}
          />
        ))}
      </div>
    )
  },

  lpViewed: (traits: { variantKey?: string } = {}) => {
    return promisedTrack(`LP Viewed (${traits?.variantKey || 'default'})`, {
      ...traits,
    })
  },

  lpClicked: (traits: { variantKey?: string } = {}) => {
    return promisedTrack(`LP Clicked (${traits?.variantKey || 'default'})`, {
      ...traits,
    })
  },

  investorOutreach: {
    landingPageViewed: (variantName, traits = {}) => {
      return promisedTrack(`Investor Outreach - ${variantName} LP Viewed`, traits)
    },
    registerViewed: (traits = {}) => {
      return promisedTrack('Investor Outreach - Register Viewed', traits)
    },
    registerSubmitted: (traits = {}) => {
      return promisedTrack('Investor Outreach - Register Submitted', traits)
    },
    pitchDeckViewed: (traits = {}) => {
      return promisedTrack('Investor Outreach - Pitch Deck Viewed', traits)
    },
    pitchDeckSubmitted: (traits = {}) => {
      return promisedTrack('Investor Outreach - Pitch Deck Submitted', traits)
    },
    purchaseViewed: (traits = {}) => {
      return promisedTrack('Investor Outreach - Purchase Viewed', traits)
    },
    purchaseSubmitted: (traits = {}) => {
      return promisedTrack('Investor Outreach - Purchase Submitted', traits)
    },
  },
}

export default tracker
